import { FormEvent, useEffect, useState } from 'react';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

/** Mirrors publicPackage() in wpcursor-backend/src/services/packageService.js */
interface Pkg {
  id: string;
  key: string;
  kind: 'plan' | 'content_pack' | 'addon';
  name: string;
  description: string | null;
  priceCents: number;
  currency: string;
  interval: 'month' | 'year' | 'one_time';
  perSite: boolean;
  credits: number;
  trialCredits: number | null;
  trialDays: number | null;
  minSites: number | null;
  maxSites: number | null;
  features: string[];
  visible: boolean;
  sortOrder: number;
  stripePriceId: string | null;
  archived: boolean;
}

interface AuditEntry {
  id: string; packageKey: string; action: string; actor: string | null; createdAt: string;
  before: Pkg | null; after: Pkg | null;
}

const KIND_LABEL: Record<Pkg['kind'], string> = { plan: 'Plan', content_pack: 'Content pack', addon: 'Add-on' };
const INTERVAL_LABEL: Record<Pkg['interval'], string> = { month: '/mo', year: '/yr', one_time: ' once' };

export const money = (cents: number) =>
  `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;

const priceLabel = (p: Pkg) =>
  p.priceCents === 0 ? 'Free' : `${money(p.priceCents)}${p.perSite ? '/site' : ''}${INTERVAL_LABEL[p.interval]}`;

/** Keys checkout and renewals can already sell. Anything else is stored but not purchasable yet. */
export const BUILT_IN_KEYS = ['free', 'standard', 'team', 'agency'];
/** Built-in grants, used to prefill Credits so a plan is never saved with 0. Agency's is per site. */
const BUILT_IN_CREDITS: Record<string, number> = { free: 5000, standard: 5000, team: 2000, agency: 2000 };
/** Agency (and any per-site plan) sits on a volume-tiered Stripe price this form cannot recreate. */
const priceLocked = (key: string, perSite: boolean) => key === 'agency' || perSite;

const optInt = (v: string): number | null => (v.trim() === '' ? null : Number(v));

export default function Packages() {
  const { can } = useAuth();
  const canWrite = can('coupons', 'write');

  const [packages, setPackages] = useState<Pkg[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Pkg | 'new' | null>(null);
  const [auditFor, setAuditFor] = useState<Pkg | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await backendApi.get<{ packages: Pkg[] }>('/license/admin/packages');
      setPackages(data.packages);
      setError('');
    } catch (err: any) {
      setError((err as ApiError).isForbidden ? 'You do not have access to packages.' : err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  async function archive(p: Pkg) {
    if (!confirm(`Retire ${p.name}? It is removed from sale; existing subscribers keep their price and keep renewing.`)) return;
    setError('');
    try {
      await backendApi.delete(`/license/admin/packages/${p.key}`);
      await load();
    } catch (err: any) { setError(err.body?.error || err.message); }
  }

  return (
    <>
      <header className="page-header">
        <div>
          <h1>Packages</h1>
          <p className="muted">
            Plans, per-site pricing, content packs and add-ons. Changing a price creates a new Stripe price and
            archives the old one; existing subscribers stay on the price they bought.
          </p>
        </div>
        <div className="row-actions">
          {canWrite && (
            <button className="btn btn-primary" onClick={() => setEditing(editing === 'new' ? null : 'new')}>
              {editing === 'new' ? 'Cancel' : 'New package'}
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="alert alert-error alert-dismiss">
          <span>{error}</span>
          <button className="alert-close" aria-label="Dismiss" onClick={() => setError('')}>×</button>
        </div>
      )}

      {editing && (
        <PackageForm
          key={editing === 'new' ? 'new' : editing.key}
          pkg={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}

      <div className="card">
        {loading ? (
          <p className="muted">Loading packages…</p>
        ) : packages.length === 0 ? (
          <p className="muted">No admin-managed packages yet. Plans keep using the built-in defaults until you create or edit one.</p>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Package</th><th>Type</th><th>Price</th><th>Credits</th><th>Sites</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p.id} className={p.visible ? '' : 'row-muted'}>
                  <td><strong>{p.name}</strong> <span className="mono muted">{p.key}</span></td>
                  <td>{KIND_LABEL[p.kind]}</td>
                  <td data-testid={`price-${p.key}`}>{priceLabel(p)}</td>
                  <td className="mono">{p.credits.toLocaleString()}{p.perSite ? '/site' : ''}</td>
                  <td className="mono">
                    {p.minSites == null && p.maxSites == null ? '—' : `${p.minSites ?? 1}–${p.maxSites ?? '∞'}`}
                  </td>
                  <td>
                    <span className={'pill ' + (p.visible ? 'pill-ok' : 'pill-off')}>{p.visible ? 'visible' : 'hidden'}</span>
                  </td>
                  <td className="right">
                    <button className="btn btn-sm" onClick={() => setAuditFor(p)}>History</button>
                    {canWrite && (
                      <>
                        {' '}
                        <button className="btn btn-sm" onClick={() => setEditing(p)}>Edit</button>
                        {' '}
                        <button className="btn btn-sm btn-danger" onClick={() => archive(p)}>Retire</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {auditFor && <History pkg={auditFor} onClose={() => setAuditFor(null)} />}
    </>
  );
}

function PackageForm({ pkg, onCancel, onSaved }: { pkg: Pkg | null; onCancel: () => void; onSaved: () => void }) {
  const isEdit = pkg !== null;
  const [f, setF] = useState({
    key: pkg?.key ?? '',
    kind: pkg?.kind ?? ('plan' as Pkg['kind']),
    name: pkg?.name ?? '',
    description: pkg?.description ?? '',
    price: pkg ? String(pkg.priceCents / 100) : '',
    interval: pkg?.interval ?? ('month' as Pkg['interval']),
    perSite: pkg?.perSite ?? false,
    credits: pkg ? String(pkg.credits) : '',
    trialCredits: pkg?.trialCredits == null ? '' : String(pkg.trialCredits),
    trialDays: pkg?.trialDays == null ? '' : String(pkg.trialDays),
    minSites: pkg?.minSites == null ? '' : String(pkg.minSites),
    maxSites: pkg?.maxSites == null ? '' : String(pkg.maxSites),
    features: (pkg?.features ?? []).join('\n'),
    visible: pkg?.visible ?? false,
    sortOrder: String(pkg?.sortOrder ?? 100),
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const priceChanged = isEdit && Math.round(Number(f.price || 0) * 100) !== pkg!.priceCents;
  const intervalChanged = isEdit && f.interval !== pkg!.interval;
  const grantChanged = isEdit && (
    Number(f.credits || 0) !== pkg!.credits
    || optInt(f.trialCredits) !== pkg!.trialCredits
    || optInt(f.trialDays) !== pkg!.trialDays
  );
  const effectiveKey = isEdit ? pkg!.key : f.key.trim();
  const locked = priceLocked(effectiveKey, f.perSite);
  const sellable = BUILT_IN_KEYS.includes(effectiveKey);
  const isPlan = (isEdit ? pkg!.kind : f.kind) === 'plan';

  function onKeyChange(key: string) {
    const builtIn = BUILT_IN_CREDITS[key.trim()];
    setF((cur) => ({ ...cur, key, credits: cur.credits === '' && builtIn ? String(builtIn) : cur.credits }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (isPlan && !(Number(f.credits) >= 1)) {
      setError('A plan must grant at least 1 credit. Enter the credits per month (per site for Agency).');
      return;
    }
    setError(''); setBusy(true);
    const body = {
      name: f.name.trim(),
      description: f.description.trim() || null,
      priceCents: Math.round(Number(f.price || 0) * 100),
      interval: f.interval,
      perSite: f.perSite,
      credits: Number(f.credits || 0),
      trialCredits: optInt(f.trialCredits),
      trialDays: optInt(f.trialDays),
      minSites: optInt(f.minSites),
      maxSites: optInt(f.maxSites),
      features: f.features.split('\n').map((s) => s.trim()).filter(Boolean),
      visible: f.visible,
      sortOrder: Number(f.sortOrder || 100),
    };
    try {
      if (isEdit) await backendApi.patch(`/license/admin/packages/${pkg!.key}`, body);
      else await backendApi.post('/license/admin/packages', { ...body, key: f.key.trim(), kind: f.kind });
      onSaved();
    } catch (err: any) {
      setError(err.body?.error || err.message);
    } finally { setBusy(false); }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">{isEdit ? `Edit ${pkg!.name}` : 'New package'}</h2>
      {error && <div className="alert alert-error">{error}</div>}
      {(priceChanged || intervalChanged) && (
        <div className="alert">
          Saving creates a new Stripe price and archives the old one. Existing subscribers stay on their current price.
        </div>
      )}
      {grantChanged && (
        <div className="alert">
          Credit and trial changes apply to every subscriber of this plan at their next renewal, including those on an older price.
        </div>
      )}
      {!sellable && f.visible && (
        <div className="alert">
          This package is not purchasable yet: checkout only sells Free, Standard, Team and Agency. It will appear on pricing pages but cannot be bought.
        </div>
      )}
      {locked && (
        <div className="alert">
          This package is priced per site on a volume-tiered Stripe price. Its price and billing interval cannot be changed here yet; credits, trial and visibility can.
        </div>
      )}
      <div className="grid-2">
        {!isEdit && (
          <>
            <label className="field">
              <span>Key (permanent, e.g. agency_per_site)</span>
              <input value={f.key} placeholder="content_pack_5k" onChange={(e) => onKeyChange(e.target.value)} />
            </label>
            <label className="field">
              <span>Type</span>
              <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as Pkg['kind'] })}>
                <option value="plan">Plan</option>
                <option value="content_pack">Content pack</option>
                <option value="addon">Add-on</option>
              </select>
            </label>
          </>
        )}
        <label className="field">
          <span>Name</span>
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </label>
        <label className="field">
          <span>Price (USD, 0 = free)</span>
          <input type="number" min="0" step="0.01" value={f.price} disabled={isEdit && locked} onChange={(e) => setF({ ...f, price: e.target.value })} />
        </label>
        <label className="field">
          <span>Billing</span>
          <select value={f.interval} disabled={isEdit && locked} onChange={(e) => setF({ ...f, interval: e.target.value as Pkg['interval'] })}>
            <option value="month">Monthly</option>
            <option value="year">Yearly</option>
            {!isPlan && <option value="one_time">One-time</option>}
          </select>
        </label>
        <label className="field">
          <span>Credits{f.perSite ? ' per site' : ''}</span>
          <input type="number" min={isPlan ? 1 : 0} required={isPlan} value={f.credits} onChange={(e) => setF({ ...f, credits: e.target.value })} />
        </label>
        <label className="field">
          <span>Trial credits (blank = plan default)</span>
          <input type="number" min="0" value={f.trialCredits} onChange={(e) => setF({ ...f, trialCredits: e.target.value })} />
        </label>
        <label className="field">
          <span>Trial days (blank = plan default)</span>
          <input type="number" min="0" value={f.trialDays} onChange={(e) => setF({ ...f, trialDays: e.target.value })} />
        </label>
        <label className="field">
          <span>Min sites (blank = none)</span>
          <input type="number" min="0" value={f.minSites} onChange={(e) => setF({ ...f, minSites: e.target.value })} />
        </label>
        <label className="field">
          <span>Max sites (blank = none)</span>
          <input type="number" min="0" value={f.maxSites} onChange={(e) => setF({ ...f, maxSites: e.target.value })} />
        </label>
        <label className="field">
          <span>Sort order</span>
          <input type="number" value={f.sortOrder} onChange={(e) => setF({ ...f, sortOrder: e.target.value })} />
        </label>
      </div>
      <label className="field">
        <span>Description</span>
        <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      </label>
      <label className="field">
        <span>Features (one per line)</span>
        <textarea rows={4} value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} />
      </label>
      <label className="check">
        <input type="checkbox" checked={f.perSite} onChange={(e) => setF({ ...f, perSite: e.target.checked })} />
        {' '}Priced per site (quantity = number of sites)
      </label>
      <label className="check">
        <input type="checkbox" checked={f.visible} onChange={(e) => setF({ ...f, visible: e.target.checked })} />
        {' '}Visible on pricing pages and in the plugin
      </label>
      <div className="row-actions">
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        {' '}
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create package'}
        </button>
      </div>
    </form>
  );
}

function History({ pkg, onClose }: { pkg: Pkg; onClose: () => void }) {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    backendApi.get<{ entries: AuditEntry[] }>(`/license/admin/packages/audit?key=${encodeURIComponent(pkg.key)}`)
      .then((d) => setEntries(d.entries))
      .catch((e: any) => setError(e.message));
  }, [pkg.key]);

  return (
    <div className="card">
      <h2 className="card-title">{pkg.name} — change history</h2>
      {error && <div className="alert alert-error">{error}</div>}
      {entries === null ? <p className="muted">Loading…</p> : entries.length === 0 ? <p className="muted">No changes recorded.</p> : (
        <table className="table">
          <thead><tr><th>When</th><th>Who</th><th>Change</th></tr></thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="muted">{new Date(e.createdAt).toLocaleString()}</td>
                <td>{e.actor ?? '—'}</td>
                <td>
                  <span className="tag">{e.action.replace('_', ' ')}</span>
                  {e.action === 'edit_price' && e.before && e.after &&
                    ` ${money(e.before.priceCents)} → ${money(e.after.priceCents)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row-actions"><button className="btn" onClick={onClose}>Close</button></div>
    </div>
  );
}
